import chevronDownDouble from "@/assets/icons/chevron-down-double.svg";
import DashboardInfos from "@/components/DashboardInfos";
import { Button } from "@/components/ui/button";
import DashboardWidgets from "@/components/widgets/dashboard/DashboardWidgets";
import useSlideOpenClose from "@/hooks/useSlideOpenClose";
import { LAYOUT } from "@/lib/layoutConstants";
import { cn } from "@/lib/utils";
import { modaleConfig } from "@/router/routes";
import assistantStore from "@/stores/assistantStore";
import dashboardStore from "@/stores/dashboardStore";
import mailStore from "@/stores/mailStore";
import { useSelector } from "@tanstack/react-store";
import { ReactNode, useEffect, useRef } from "react";

interface SlideOutModalProps {
    mainModalContent: ReactNode;
    onRequestOpen: () => void;
    assistantModalContent?: ReactNode;
}

const SlideOutModal = ({
    mainModalContent,
    onRequestOpen,
    assistantModalContent,
}: SlideOutModalProps) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const isAssistantOpen = useSelector(assistantStore, (s) => s.isOpen);
    const urlPath = window.location.pathname;
    const isMainModalOpen =
        urlPath !== modaleConfig.pathWhenModalClosed || isAssistantOpen;

    const { isVisible: isModalVisible, isExpanded: isModalExpanded } =
        useSlideOpenClose(isMainModalOpen);
    const {
        isVisible: isAssistantSlideVisible,
        isExpanded: isAssistantSlideExpanded,
    } = useSlideOpenClose(isAssistantOpen);
    const isWidgetExpanded = useSelector(
        dashboardStore,
        (s) => s.expandedWidget !== null,
    );
    // The full-screen reply overlay renders inside the panels below, whose
    // fixed+z-index creates a stacking context: the panel must pass above the
    // header (z-header) or its buttons would show through the overlay.
    const isMailInputFullScreen = useSelector(
        mailStore,
        (s) => s.isMailInputFullScreen,
    );
    const panelZIndexClass = isMailInputFullScreen
        ? "z-fullscreen"
        : "z-modal-panel";

    useEffect(() => {
        if (isModalExpanded) {
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "";
        }

        return () => {
            document.body.style.overflow = "";
        };
    }, [isModalExpanded]);

    useEffect(() => {
        const handleWheel = (event: WheelEvent): void => {
            if (
                event.deltaY <= 0 ||
                isModalExpanded ||
                isWidgetExpanded ||
                event.ctrlKey
            ) {
                return;
            }

            const isInsideScrollArea = !!(event.target as Element)?.closest?.(
                ".dashboard-scroll-viewport",
            );

            if (!isInsideScrollArea) {
                onRequestOpen();
            }
        };

        const container = containerRef.current;
        if (container) {
            container.addEventListener("wheel", handleWheel);
        }

        return () => {
            if (container) {
                container.removeEventListener("wheel", handleWheel);
            }
        };
    }, [isModalExpanded, isWidgetExpanded, onRequestOpen]);

    return (
        <div ref={containerRef} className="relative flex h-full">
            <div className="mx-4 h-full flex-col items-start lg:mx-16">
                <DashboardInfos isModalExpanded={isModalExpanded} />
            </div>
            <div className="ml-auto mr-4 h-full flex-col items-start lg:mr-16">
                <DashboardWidgets />
            </div>

            <Button
                onClick={onRequestOpen}
                size="circle"
                variant="circleFloating"
            >
                <img
                    alt="chevron-down-double"
                    className="w-5"
                    src={chevronDownDouble}
                />
            </Button>

            {/* Invisible backdrop sitting between the dashboard and the modal panel.
                It intercepts touch events so swipe gestures on the visible portion
                of the dashboard cannot reach and scroll it. See tailwind.config.js
                for the z-index scale. */}
            {isModalVisible && (
                <div
                    aria-hidden="true"
                    className={cn(
                        "fixed inset-0 z-modal-backdrop transition-opacity duration-300",
                        LAYOUT.modalTopWithoutNotification,
                        isModalExpanded
                            ? "pointer-events-auto opacity-100"
                            : "pointer-events-none opacity-0",
                    )}
                    onTouchMove={(e) => e.preventDefault()}
                    onTouchStart={(e) => e.stopPropagation()}
                />
            )}

            <div
                className={cn(
                    "fixed bottom-0 left-1/2 flex w-screen -translate-x-1/2 flex-col justify-end transition-all duration-300",
                    panelZIndexClass,
                    LAYOUT.modalTopWithoutNotification,
                    !isModalVisible && "hidden",
                )}
            >
                <div
                    className={`h-full w-full rounded-t-[24px] bg-surface-background ${isModalExpanded ? "translate-y-0" : "translate-y-full"} transition-all duration-1000`}
                >
                    <div className="h-full w-full items-center rounded-t-[24px] bg-surface-background">
                        {mainModalContent}
                    </div>
                </div>
            </div>

            {assistantModalContent != null && (
                <div
                    className={cn(
                        "fixed bottom-0 left-1/2 flex w-screen -translate-x-1/2 flex-col justify-end transition-all duration-300",
                        panelZIndexClass,
                        LAYOUT.modalTopWithoutNotification,
                        !isAssistantSlideVisible && "hidden",
                    )}
                >
                    <div
                        className={`h-full w-full rounded-t-[24px] border-t border-gray-500 bg-surface-background transition-all duration-1000 ${isAssistantSlideExpanded ? "translate-y-0 shadow-[0_-8px_20px_-8px_grey]" : "translate-y-full shadow-none"}`}
                    >
                        {assistantModalContent}
                    </div>
                </div>
            )}
        </div>
    );
};

export default SlideOutModal;
